import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * bn-panel is the lighter, chromeless section surface (no elevation) with an
 * optional uppercase eyebrow heading. Use it to group related controls or
 * metadata inside a page — an instance's size class, a generation's summary, a
 * policy editor's side notes. For a bordered, elevated container use bn-card.
 *
 * Properties:
 * - `eyebrow` — short uppercase section label rendered as a heading. Omit it
 *   for an unlabelled surface.
 *
 *   <bn-panel eyebrow="Blast radius">
 *     <p>This publish restarts the instance.</p>
 *   </bn-panel>
 */
@customElement("bn-panel")
export class BnPanel extends LitElement {
  /** Short uppercase section label; rendered as an <h3> when set. */
  @property() eyebrow = "";

  static override styles = css`
    :host {
      display: block;
      background: var(--bino-surface-subtle);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      padding: var(--bino-space-md);
      color: var(--bino-text);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
    }
    .eyebrow {
      margin: 0 0 var(--bino-space-sm);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bino-primary);
    }
  `;

  override render() {
    return html`
      ${this.eyebrow ? html`<h3 class="eyebrow">${this.eyebrow}</h3>` : ""}
      <slot></slot>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-panel": BnPanel;
  }
}
